import { ShieldCheck, ShieldQuestion, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// V9.21 — Sprint UX Simplification : libellés précis et non ambigus.
// - "Déclaratif" n'est plus affiché comme un badge visible (état par défaut, silencieux).
// - Les niveaux supérieurs indiquent CE QUI a été vérifié (source / affiliation) et
//   par QUI, sans jamais laisser penser à une reconnaissance de "sportif de haut niveau".
const LEVELS = {
  declaratif:        { label: "Déclaratif",              cls: "bg-muted text-foreground border", icon: ShieldQuestion, desc: "Information déclarée par l'utilisateur, non vérifiée." },
  source:            { label: "Profil sourcé",           cls: "bg-[hsl(var(--aito-lagoon))] text-black", icon: ShieldCheck, desc: "Information appuyée par une source publique (presse, fédé, résultats)." },
  club_valide:       { label: "Affiliation validée",     cls: "bg-[hsl(var(--aito-gold))] text-black", icon: ShieldCheck, desc: "Affiliation confirmée par le club concerné." },
  federation_valide: { label: "Vérifié par la fédération", cls: "bg-black text-white border border-[hsl(var(--aito-gold))]", icon: ShieldCheck, desc: "Information confirmée par la fédération." },
  officiel_copf:     { label: "Vérifié par le COPF",     cls: "bg-black text-white border-2 border-[hsl(var(--aito-gold))]", icon: ShieldCheck, desc: "Information vérifiée par le COPF. N'implique pas de reconnaissance officielle du statut de sportif de haut niveau." },
};

/**
 * V9.21 : par défaut, le badge "Déclaratif" est masqué (silencieux) pour éviter
 * un décor visuel générique sur presque toutes les fiches. Il reste possible de
 * l'afficher explicitement avec `showDeclarative`.
 */
export function ValidationShield({ level = "declaratif", className, showDeclarative = false }) {
  if (level === "declaratif" && !showDeclarative) return null;
  const data = LEVELS[level] || LEVELS.declaratif;
  const Icon = data.icon;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-testid="validation-shield" className={cn("inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium", data.cls, className)}>
            <Icon className="h-3 w-3" /> {data.label}
          </span>
        </TooltipTrigger>
        <TooltipContent data-testid="validation-shield-tooltip">{data.desc}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function ConfidenceDot({ score = null }) {
  if (score == null) return null;
  const pct = Math.round(score * 100);
  let color = "#9CA3AF";
  if (pct >= 80) color = "hsl(152 55% 38%)";
  else if (pct >= 60) color = "hsl(43 74% 52%)";
  else color = "hsl(8 84% 55%)";
  return (
    <span className="inline-flex items-center gap-1.5 text-[11px] font-mono" data-testid="confidence-dot">
      <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: color }} /> {pct}%
    </span>
  );
}
